import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useHMG } from "../../../HMGContext";

/**
 * Common Discard Changes Dialog Component
 * Handles both plan version discard and COA mapping discard scenarios
 * 
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {Function} setOpenDiscardChangesModal - Function to close the dialog
 * @param {Function} onClose - Optional callback when dialog is closed
 * @param {Function} onSubmit - Callback when user confirms discard
 * @param {string} planName - Optional plan name to include in title (for plan version discard)
 * @param {string} titleText - Custom title text (optional, defaults based on planName)
 */
const DiscardChangesDialog = ({ 
  isOpen, 
  setOpenDiscardChangesModal, 
  onClose, 
  onSubmit, 
  planName,
  titleText
}) => {
  const { setCurrentModalName } = useHMG();
  
  // Determine defaults based on whether planName is provided
  const isPlanVersion = Boolean(planName);
  const defaultTitleText = titleText || (isPlanVersion 
    ? `Are you sure you want to discard the unsaved changes in ${planName}?`
    : "Are you sure you want to discard the unsaved changes"
  );
  
  const handleClose = () => {
    setOpenDiscardChangesModal(false);
    setCurrentModalName(null);
    if (onClose) onClose();
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit();
    setCurrentModalName(null);
    handleClose();
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            padding: "16px 16px 16px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#212B36",
                width: "98%",
                lineHeight: "30px"
              }}>
              {defaultTitleText}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <ClearIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "16px" }}>
          <Typography
            id="discard-dialog-description"
            sx={{
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: 400,
            }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "16px",
          }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            className="!font-bold !border-[#919EAB52]"
            >
            No, don't discard changes
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="error"
            sx={{ 
              bgcolor: "#FF5630",
              '&:hover': {
                backgroundColor: '#FF5630',
                boxShadow: 'none',
              },
            }}>
            Yes, discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DiscardChangesDialog;
